import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';

import { apiGet } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { Card, SectionHeader } from '@/components/ui/Card';
import { Money } from '@/components/ui/Money';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { ErrorState, LoadingState } from '@/components/ui/States';
import type { DashboardSnapshot } from '@/types/api';
import { fmtDate } from '@/utils/format';

/**
 * Day-end closing — one printable page the owner reviews before
 * locking up. We just lean on the dashboard snapshot endpoint, which
 * already aggregates the four numbers we want, and add a print button
 * styled with `print:` Tailwind utilities.
 */
export function DayEndPage() {
  const query = useQuery({
    queryKey: ['report', 'day-end'],
    queryFn: () => apiGet<DashboardSnapshot>(endpoints.reportDashboard),
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        title="Couldn't load day-end"
        description={(query.error as Error)?.message}
        onRetry={() => query.refetch()}
      />
    );
  }
  const d = query.data;

  return (
    <div className="print:bg-white print:p-0">
      <PageHeader
        back
        title="Day-end closing"
        description={fmtDate(new Date())}
        actions={
          <Button
            variant="outline"
            iconLeft={<Printer className="h-4 w-4" />}
            onClick={() => window.print()}
            className="print:hidden"
          >
            Print
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 print:grid-cols-4">
        <StatCard label="Today's revenue" value={<Money value={d.today_revenue ?? 0} />} />
        <StatCard label="Bills" value={d.today_bills ?? 0} />
        <StatCard
          label="Outstanding dues"
          value={<Money value={d.pending_dues ?? 0} />}
          deltaTone={(d.pending_dues ?? 0) > 0 ? 'warn' : 'success'}
          delta={(d.pending_dues ?? 0) > 0 ? 'Send reminders tomorrow' : 'All cleared'}
        />
        <StatCard
          label="Low stock"
          value={d.low_stock_count ?? 0}
          deltaTone={(d.low_stock_count ?? 0) > 0 ? 'warn' : 'success'}
          delta={(d.low_stock_count ?? 0) > 0 ? 'Restock list ready' : 'Nothing to restock'}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <SectionHeader title="Tomorrow's checklist" />
          <ol className="space-y-2 text-sm list-decimal list-inside text-ink">
            <li>Send reminders to customers with overdue dues.</li>
            <li>Restock items flagged as low.</li>
            <li>Run a quick stock audit if it's been more than a week.</li>
            <li>Reconcile cash drawer with today's revenue total.</li>
            <li>Lock up + back up data (auto-syncs in the background).</li>
          </ol>
        </Card>
        <Card>
          <SectionHeader title="Notes" />
          <textarea
            placeholder="Anything to remember tomorrow?"
            rows={6}
            className="w-full rounded-xl bg-surface border border-line p-3 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:outline-none"
          />
          <p className="text-xs text-ink-soft mt-1">
            Notes are local to this browser — the panel doesn't sync them yet.
          </p>
        </Card>
      </div>
    </div>
  );
}
